import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, CreditCard, FileText, AlertTriangle, CheckCircle } from 'lucide-react';

const TermsPage: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 py-16 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <Link
              to="/signup"
              className="inline-flex items-center mb-6 text-cyan-300 hover:text-cyan-200 transition"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to Sign Up
            </Link>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Terms and Conditions
            </h1>
            <p className="text-xl md:text-2xl text-cyan-200">
              Please read these terms carefully before using our services
            </p>
            <div className="mt-6 text-sm text-cyan-300">
              Last Updated: August 6, 2025
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div {...fadeInUp} className="space-y-12">

          {/* Introduction */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-400/5 to-purple-400/5 border border-cyan-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-cyan-400 mr-3" />
              <h2 className="text-2xl font-bold text-cyan-100">1. Introduction</h2>
            </div>
            <div className="text-cyan-200 space-y-4">
              <p>
                Welcome to SEWA Education Services ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of our educational consultancy services, website, and platform. By accessing or using our services, you agree to be bound by these Terms.
              </p>
              <p>
                SEWA Education Services provides educational consultation, admission guidance, and related services to students seeking admission in various educational institutions in India and abroad.
              </p>
            </div>
          </div>

          {/* Student Information Collection & Sharing */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-yellow-400/5 to-orange-400/5 border border-yellow-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <Users className="h-6 w-6 text-yellow-400 mr-3" />
              <h2 className="text-2xl font-bold text-yellow-100">2. Student Information Collection & Sharing</h2>
            </div>
            <div className="text-yellow-200 space-y-4">
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
                  <h3 className="font-semibold text-yellow-300">Important Notice</h3>
                </div>
                <p className="text-yellow-200">
                  By using our services, you explicitly consent to the collection, processing, and sharing of your personal and academic information as outlined below.
                </p>
              </div>
              
              <h3 className="text-lg font-semibold text-yellow-300">2.1 Information We Collect</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal details: Name, contact information, address, date of birth</li>
                <li>Academic records: Transcripts, certificates, test scores, educational history</li>
                <li>Financial information: Family income, financial documents for loan processing</li>
                <li>Identity documents: Passport, ID cards, photographs</li>
                <li>Preferences: Course choices, university preferences, career goals</li>
                <li>Communication records: Emails, calls, consultation notes</li>
              </ul>

              <h3 className="text-lg font-semibold text-yellow-300">2.2 Information Sharing with Educational Institutions</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Colleges and Universities:</strong> We will share your complete academic and personal profile with educational institutions for admission purposes.</li>
                <li><strong>Admission Processing:</strong> Your information may be shared with multiple institutions simultaneously to maximize admission opportunities.</li>
                <li><strong>Third-Party Verification:</strong> Educational institutions may verify your information through independent channels.</li>
                <li><strong>Long-term Storage:</strong> Colleges may retain your information for regulatory compliance and future reference.</li>
              </ul>

              <h3 className="text-lg font-semibold text-yellow-300">2.3 Additional Information Sharing</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Government agencies for visa processing and regulatory compliance</li>
                <li>Financial institutions for education loan processing</li>
                <li>Scholarship organizations and funding bodies</li>
                <li>Accommodation providers and student services</li>
                <li>Legal authorities when required by law</li>
              </ul>
            </div>
          </div>

          {/* Payment Terms & Non-Refundable Policy */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-red-400/5 to-pink-400/5 border border-red-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <CreditCard className="h-6 w-6 text-red-400 mr-3" />
              <h2 className="text-2xl font-bold text-red-100">3. Payment Terms & Non-Refundable Policy</h2>
            </div>
            <div className="text-red-200 space-y-4">
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                  <h3 className="font-semibold text-red-300">Non-Refundable Policy</h3>
                </div>
                <p className="text-red-200 font-semibold">
                  ALL PAYMENTS MADE TO SEWA EDUCATION SERVICES ARE STRICTLY NON-REFUNDABLE UNDER ANY CIRCUMSTANCES.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-red-300">3.1 Service Fees</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Consultation Fees:</strong> Charged for initial assessment and counseling sessions</li>
                <li><strong>Application Processing Fees:</strong> For preparing and submitting applications</li>
                <li><strong>Document Verification Fees:</strong> For authentication and verification services</li>
                <li><strong>Premium Services:</strong> Additional charges for priority processing and exclusive services</li>
                <li><strong>Third-party Charges:</strong> University application fees, courier charges, translation costs</li>
              </ul>

              <h3 className="text-lg font-semibold text-red-300">3.2 No Refund Scenarios</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Application rejection by educational institutions</li>
                <li>Visa denial or processing delays</li>
                <li>Change of mind by the student or guardian</li>
                <li>Inability to meet admission requirements</li>
                <li>Withdrawal from the admission process</li>
                <li>Failure to secure desired course or institution</li>
                <li>Any circumstances beyond our control</li>
              </ul>

              <h3 className="text-lg font-semibold text-red-300">3.3 Payment Terms</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>All fees must be paid in advance before service delivery</li>
                <li>Payments can be made through approved payment gateways only</li>
                <li>Additional charges may apply for international transactions</li>
                <li>Service delivery begins only after full payment confirmation</li>
              </ul>
            </div>
          </div>

          {/* Service Limitations */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-purple-400/5 to-indigo-400/5 border border-purple-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-purple-400 mr-3" />
              <h2 className="text-2xl font-bold text-purple-100">4. Service Limitations & Disclaimers</h2>
            </div>
            <div className="text-purple-200 space-y-4">
              <h3 className="text-lg font-semibold text-purple-300">4.1 No Admission Guarantee</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>We do not guarantee admission to any educational institution</li>
                <li>Admission decisions are solely at the discretion of educational institutions</li>
                <li>We provide guidance and support but cannot influence admission outcomes</li>
                <li>Success depends on individual merit, qualifications, and institutional requirements</li>
              </ul>

              <h3 className="text-lg font-semibold text-purple-300">4.2 Information Accuracy</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Students are responsible for providing accurate and complete information</li>
                <li>Any false or misleading information may result in application rejection</li>
                <li>We are not liable for consequences of inaccurate student-provided information</li>
                <li>Document verification is based on available resources and methods</li>
              </ul>

              <h3 className="text-lg font-semibold text-purple-300">4.3 External Factors</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Immigration policy changes and visa requirements</li>
                <li>Educational institution policy modifications</li>
                <li>Government regulations and compliance requirements</li>
                <li>Economic conditions affecting education sector</li>
                <li>Force majeure events (natural disasters, pandemics, etc.)</li>
              </ul>
            </div>
          </div>

          {/* User Responsibilities */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-green-400/5 to-blue-400/5 border border-green-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-400 mr-3" />
              <h2 className="text-2xl font-bold text-green-100">5. User Responsibilities</h2>
            </div>
            <div className="text-green-200 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, complete, and truthful information</li>
                <li>Submit all required documents in specified formats and timelines</li>
                <li>Respond promptly to requests for additional information</li>
                <li>Meet all deadlines for applications and document submissions</li>
                <li>Comply with educational institution requirements and policies</li>
                <li>Maintain confidentiality of login credentials and account access</li>
                <li>Report any changes in personal or academic status immediately</li>
                <li>Pay all fees and charges as per agreed terms</li>
                <li>Cooperate during the application and admission process</li>
              </ul>
            </div>
          </div>

          {/* Privacy & Data Protection */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-400/5 to-teal-400/5 border border-cyan-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-cyan-400 mr-3" />
              <h2 className="text-2xl font-bold text-cyan-100">6. Privacy & Data Protection</h2>
            </div>
            <div className="text-cyan-200 space-y-4">
              <p>
                While we implement security measures to protect your data, you acknowledge and consent to the following:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your information will be stored on our servers and partner systems</li>
                <li>Data may be transferred across borders for service delivery</li>
                <li>Educational institutions have their own privacy policies for shared data</li>
                <li>We may use your information for service improvement and analytics</li>
                <li>Anonymized data may be used for research and statistical purposes</li>
                <li>You have limited rights to data deletion due to regulatory requirements</li>
              </ul>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-pink-400/5 to-rose-400/5 border border-pink-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-pink-400 mr-3" />
              <h2 className="text-2xl font-bold text-pink-100">7. Intellectual Property</h2>
            </div>
            <div className="text-pink-200 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>All content, logos, and materials on our platform are our intellectual property</li>
                <li>You may not reproduce, distribute, or modify our content without permission</li>
                <li>Educational materials and guides remain our proprietary assets</li>
                <li>User-generated content may be used by us for service improvement</li>
                <li>Violation of intellectual property rights may result in legal action</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-orange-400/5 to-red-400/5 border border-orange-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-orange-400 mr-3" />
              <h2 className="text-2xl font-bold text-orange-100">8. Limitation of Liability</h2>
            </div>
            <div className="text-orange-200 space-y-4">
              <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                <p className="font-semibold">
                  SEWA EDUCATION SERVICES SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM THE USE OF OUR SERVICES.
                </p>
              </div>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are not responsible for admission rejections or visa denials</li>
                <li>No liability for financial losses due to failed applications</li>
                <li>Not responsible for third-party service failures or delays</li>
                <li>Maximum liability limited to the fees paid for our services</li>
                <li>No liability for indirect or consequential damages</li>
              </ul>
            </div>
          </div>

          {/* Termination */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-gray-400/5 to-slate-400/5 border border-gray-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-gray-400 mr-3" />
              <h2 className="text-2xl font-bold text-gray-100">9. Termination</h2>
            </div>
            <div className="text-gray-200 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>We reserve the right to terminate services at our discretion</li>
                <li>Termination for breach of terms or misuse of services</li>
                <li>No refund of fees upon termination for any reason</li>
                <li>Student obligations continue even after service termination</li>
                <li>Data retention policies apply post-termination</li>
              </ul>
            </div>
          </div>

          {/* Governing Law */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-indigo-400/5 to-blue-400/5 border border-indigo-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-indigo-400 mr-3" />
              <h2 className="text-2xl font-bold text-indigo-100">10. Governing Law & Jurisdiction</h2>
            </div>
            <div className="text-indigo-200 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>These terms are governed by the laws of India</li>
                <li>All disputes subject to the jurisdiction of Tamil Nadu courts</li>
                <li>Mandatory arbitration for commercial disputes</li>
                <li>No class action lawsuits permitted</li>
                <li>Legal proceedings to be conducted in English language</li>
              </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-400/5 to-purple-400/5 border border-cyan-400/10 rounded-xl shadow-md p-8">
            <div className="flex items-center mb-4">
              <Users className="h-6 w-6 text-cyan-400 mr-3" />
              <h2 className="text-2xl font-bold text-cyan-100">11. Contact Information</h2>
            </div>
            <div className="text-cyan-200 space-y-4">
              <p>For questions about these Terms and Conditions, contact us at:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p><strong>Email:</strong> infonammasewa@gmail.com</p>
                  <p><strong>Phone:</strong> +91 77086 77132</p>
                  <p><strong>Address:</strong> SEWA International Office</p>
                  <p>Tamil Nadu, India</p>
                </div>
                <div>
                  <p><strong>Business Hours:</strong> Mon - Sat: 9:00 AM - 7:00 PM</p>
                  <p><strong>Legal Department:</strong> infonammasewa@gmail.com</p>
                  <p><strong>Response Time:</strong> 2-3 business days</p>
                </div>
              </div>
            </div>
          </div>

          {/* Acceptance Notice */}
          <div className="backdrop-blur-lg bg-gradient-to-br from-green-400/10 to-blue-400/10 border border-green-400/20 rounded-xl shadow-md p-8 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-green-100 mb-4">Terms Acceptance</h3>
            <p className="text-green-200 mb-4">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
            <p className="text-sm text-green-300">
              These terms constitute a legally binding agreement between you and SEWA Education Services.
            </p>
          </div>

        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;
